import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth';
import { XtreedLogo } from '@/components/ui/XtreedLogo';
import { useTenant } from '@/providers/TenantProvider';
import { LanguageSelector } from '@/components/LanguageSelector';
import {
  XtreedVideoField,
  varianteDaRota,
} from '@/components/ui/XtreedVideoField';
import { useStudentTheme } from '@/hooks/useStudentTheme';

/**
 * Moldura das telas de entrada (login, recuperação de senha, aceite de termos).
 *
 * Esta tela é servida para os alunos de TODOS os clientes, então ela não pode
 * afirmar nada sobre o negócio de nenhum deles. A versão anterior trazia a
 * peça de venda de uma escola de programação — números de empregabilidade,
 * trilhas de front-end, logos de contratantes — que aparecia igual para um
 * estúdio de meditação e para um curso de finanças.
 *
 * O painel da direita é campo de marca, sem texto: um dos três loops da
 * árvore da Xtreed. Qual deles vem da URL — /login, /login/2, /login/3 —, o que
 * permite comparar as peças no ambiente real. Quando o cliente tem banner
 * próprio, é o banner dele que ocupa o espaço.
 */
export function AuthLayout() {
  const { t } = useTranslation('layout');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const tenant = useTenant();
  const { pathname } = useLocation();

  useStudentTheme();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Formulário */}
      <div className="relative flex flex-1 items-center justify-center px-4 py-8 sm:px-6 lg:p-12">
        <LanguageSelector className="absolute top-4 right-4 z-20 sm:top-6 sm:right-6" />
        <div className="relative z-10 mx-auto w-full max-w-md">
          <div className="mb-8 flex animate-fade-in justify-center sm:mb-10">
            <XtreedLogo size="lg" showText />
          </div>

          <div
            className="animate-fade-in-up"
            style={{ animationDelay: '100ms' }}
          >
            <Outlet />
          </div>

          <div className="mt-8 animate-fade-in text-center text-sm text-muted-foreground">
            <p>
              <Trans
                t={t}
                i18nKey="auth.poweredBy"
                components={{
                  brand: <span className="font-semibold text-primary-ink" />,
                }}
              />
            </p>
          </div>
        </div>
      </div>

      {/* Campo de marca */}
      <div className="relative hidden flex-1 overflow-hidden lg:block">
        {tenant?.banner ? (
          <img
            src={tenant.banner}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <XtreedVideoField
            variante={varianteDaRota(pathname)}
            className="absolute inset-0"
          />
        )}
      </div>
    </div>
  );
}
